import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { TablePagination, TablePaginationProps, useTheme } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { AtonUnitComponent, AtonUnitDetails } from "~/api/GeneratedApi";
import GenericTable, {
  CustomColumn,
  CustomTableProps,
} from "~/components/GenericTable";
import PaddedBox from "~/components/PaddedBox";
import { useDateFormatter } from "~/utils/hooks/useDateFormatter";

interface Props {
  atonData: AtonUnitDetails;
  small?: boolean;
}

const AtoNActionsTab = ({ atonData, small }: Props): JSX.Element => {
  const { t } = useTranslation("aton-component");
  const theme = useTheme();
  const dateFormatter = useDateFormatter();

  const { components } = atonData;

  const columns: CustomColumn<AtonUnitComponent>[] = React.useMemo(
    () => [
      {
        title: t("class"),
        field: "component.class.name",
      },
      {
        title: t("component"),
        field: "component.name",
      },
      {
        title: t("serialNumber"),
        field: "serialNumber",
      },
      {
        title: t("temporary"),
        field: "temporary",
        render: ({ temporary }) => (temporary ? <CheckIcon /> : <CloseIcon />),
      },
      {
        title: t("start-date"),
        field: "inEffectStartDate",
        render: ({ inEffectStartDate }) =>
          inEffectStartDate
            ? dateFormatter({ date: inEffectStartDate, format: "P" })
            : null,
      },
      {
        title: t("end-date"),
        field: "inEffectEndDate",
        render: ({ inEffectEndDate }) =>
          inEffectEndDate
            ? dateFormatter({ date: inEffectEndDate, format: "P" })
            : null,
      },
      {
        title: t("information"),
        field: "memo",
      },
    ],
    [dateFormatter, t],
  );

  const tableProps: CustomTableProps<AtonUnitComponent> = React.useMemo(
    () => ({
      title: t("aton:list-components"),
      ...(small
        ? {
            options: {
              pageSizeOptions: [5, 10, 15, 25, 50],
              pageSize: 10,
            },
            components: {
              Container: (props) => (
                <div
                  {...props}
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    border: "none",
                  }}
                />
              ),
              Pagination: (props: TablePaginationProps) => {
                const { style } = props;
                return (
                  <TablePagination
                    {...props}
                    style={{
                      borderBottomRightRadius: theme.shape.borderRadius,
                      ...style,
                    }}
                  />
                );
              },
            },
          }
        : {}),
    }),
    [small, t, theme.shape.borderRadius],
  );

  const table = React.useMemo(
    () => (
      <GenericTable
        data={components ?? []}
        columns={columns}
        tableProps={tableProps}
      />
    ),
    [columns, components, tableProps],
  );

  return small ? table : <PaddedBox>{table}</PaddedBox>;
};

export default AtoNActionsTab;
